import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCaretDown, faCaretLeft } from '@fortawesome/pro-duotone-svg-icons';
import { faMessagesQuestion, faMicrochipAi } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { Container, Nav, NavDropdown, Navbar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { NavLink } from 'react-router-dom';

import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import { getAllInvolvedUrls } from 'components/desktop/_general/MainNavigationLeft/MainNavigationLeftHandlers';
import { isItemSelected } from 'components/desktop/_general/MainNavigationLeft/MainNavigationLeftHandlers';
import { SidebarItem } from 'components/desktop/_general/MainNavigationLeft/constants/SidebarItems';
import SIDEBAR_ITEMS from 'components/desktop/_general/MainNavigationLeft/constants/SidebarItems';
import useClassNameForVisibilityChange from 'components/desktop/_general/MainNavigationLeft/hooks/useClassNameForVisibiltyChange';
import { usePathname } from 'components/desktop/_general/MainNavigationLeft/hooks/usePathname';
import PERMISSIONS from 'enums/permissions';
import { getPicturePath } from 'functions/getPicturePath';
import {
	areEntitlementsInUserEntitlements,
	arePermissionsInUserPermissions,
} from 'functions/tokenFunctions';
import { isUserWriter } from 'functions/tokenFunctions';
import { isUserAdmin } from 'functions/tokenFunctions';
import { RootState } from 'reducers/rootReducer';
import { UserCornerInfo } from 'types/User/UserCornerInfo/UserCornerInfo';
import SidebarState from 'types/_general/SidebarState';

import NavigationLanguageSwitch from '../../NavigationLanguage/NavigationLanguageSwitch';
import UserAvatar from '../../UserAvatar/UserAvatar';
import '../MainNavigationLeft.scss';
import ShoppingCartQuickInfo from './ShoppingCartQuickInfo';

interface IProps {
	sidebarState: SidebarState;
}

export default function Body(props: IProps) {
	const { t } = useTranslation();
	const visible = useClassNameForVisibilityChange(props.sidebarState);
	const [openItemIds, setOpenItemIds] = useState<string[]>([]);
	const pathname: string = usePathname();
	const navigate = useNavigate();
	const location = useLocation();
	const reduxMenuBarOrder: string[] = useSelector((state: RootState) => state.menuBarOrder);

	const [showAltIcon, setShowAltIcon] = useState(false);

	useEffect(() => {
		const interval = setInterval(() => {
			setShowAltIcon((v) => !v);
		}, 2000);
		return () => clearInterval(interval);
	}, []);

	const renderIconItem = (item: SidebarItem, level: number): JSX.Element => {
		const size: string = '24px';
		let marginLeft: string = '0px';
		if (props.sidebarState !== 'closed') {
			marginLeft = 20 * level + 'px';
		}
		if (item.icon) {
			return (
				<FontAwesomeIcon
					icon={item.icon}
					style={{ height: size, width: size, marginLeft: marginLeft, color: '#6066c1' }}
				/>
			);
		}
		return <></>;
	};

	const openLink = (url: string): void => {
		navigate(url);
	};

	const renderIconCaret = (item: SidebarItem): JSX.Element => {
		const open: boolean = Boolean(openItemIds.includes(item.url) && item.children?.length);
		return (
			<FontAwesomeIcon icon={open ? (faCaretDown as IconProp) : (faCaretLeft as IconProp)} />
		);
	};

	const clickItem = (item: SidebarItem): void => {
		if (!item.children?.length) return;
		const allInvolvedUrls: string[] = getAllInvolvedUrls(SIDEBAR_ITEMS, item.url);
		if (item.defaultUrl) {
			openLink(item.defaultUrl);
		}
		setOpenItemIds([item.url]);
	};

	const renderNavLink = (item: SidebarItem, level: number): JSX.Element => {
		return (
			<div
				className={'item-display ' + visible}
				role="button"
				onClick={() => openLink(item.url)}
			>
				{renderIconItem(item, level)}
				<div className="item-title">{t(item.translationKey)}</div>
			</div>
		);
	};

	const renderNavContainer = (item: SidebarItem, level: number): JSX.Element => {
		return (
			<>
				<div
					className={'item-display ' + visible}
					role="button"
					onClick={(e: React.MouseEvent<HTMLDivElement>) => clickItem(item)}
				>
					{renderIconItem(item, level)}
					<div className="item-title">{t(item.translationKey)}</div>
					<div className="carret">{renderIconCaret(item)}</div>
				</div>
				<ul>
					{item.children?.map((item: SidebarItem, i: number) => {
						return renderMenuItem(item, i);
					})}
				</ul>
			</>
		);
	};

	const getSelectedClass = (item: SidebarItem): string => {
		if (isItemSelected(item, pathname)) return 'selected';
		return '';
	};

	const itShouldRender = (item: SidebarItem): boolean => {
		return checkEntitlements(item) && checkPermissions(item);
	};

	const checkEntitlements = (item: SidebarItem): boolean => {
		if (!item.entitlements.length) return true;
		return areEntitlementsInUserEntitlements(item.entitlements);
	};

	const checkPermissions = (item: SidebarItem): boolean => {
		if (!item.permissions.length) return true;
		if (item.permissions.includes(PERMISSIONS.ADMINISTRATE) && isUserAdmin()) return true;
		if (item.permissions.includes(PERMISSIONS.WRITE) && isUserWriter()) return true;
		if (arePermissionsInUserPermissions(item.permissions)) return true;
		return false;
	};

	const renderMenuItem = (item: SidebarItem, idx: number | string): JSX.Element => {
		const key = `${item.url}-${idx}`;

		// You can swap this out for your i18n function, e.g. t(item.translationKey)
		const title = (
			<>
				{/* <FontAwesomeIcon icon={item.icon} className="me-2" /> */}
				{t(item.translationKey)}
			</>
		);

		if (item.children && item.children.length > 0) {
			return (
				<NavDropdown
					active={location.pathname.includes(item.url)}
					title={title}
					id={`nav-dropdown-${key}`}
					key={key}
					menuVariant="light"
				>
					{item.children.map((child, cidx) =>
						// recurse so grandchildren become nested dropdowns (if any)
						renderMenuItem(child, `${key}-${cidx}`)
					)}
				</NavDropdown>
			);
		}

		return (
			<Nav.Link
				key={key}
				active={location.pathname.includes(item.url)}
				onClick={(e) => openLink(item.url)}
			>
				{title}
			</Nav.Link>
		);

		/* return (
			<RenderIf condition={itShouldRender(item)} key={index}>
				<li
					className={[
						openItemIds.includes(item.url) ? 'open' : '',
						'level-' + level,
						getSelectedClass(item),
						item.children?.length ? 'selected-container' : '',
					].join(' ')}
				>
					<RenderIf condition={!item.hidden}>
						{item.children?.length
							? renderNavContainer(item, level)
							: renderNavLink(item, level)}
					</RenderIf>
				</li>
			</RenderIf>
		); */
	};

	const sortSidebarItems = (): SidebarItem[] => {
		const result: SidebarItem[] = [];
		result.push(SIDEBAR_ITEMS[0]);

		(reduxMenuBarOrder ?? []).forEach((e: string) => {
			const index = SIDEBAR_ITEMS.findIndex((x) =>
				x.url.toLowerCase().includes(e.toLowerCase())
			);
			result.push(SIDEBAR_ITEMS[index]);
		});
		result.push(SIDEBAR_ITEMS[1]);

		return result;
	};

	const reduxUser: UserCornerInfo = useSelector((state: RootState) => state.user);

	return (
		<Navbar collapseOnSelect expand="lg">
			<Container>
				<Navbar.Toggle aria-controls="responsive-navbar-nav" />
				<Navbar.Brand>
					<div style={{ display: 'flex', alignItems: 'center', marginRight: '0.5rem' }}>
						<img
							src={'/img/logo/logo_sm.png'}
							style={{ height: '2rem', boxShadow: 'none' }}
							className="d-inline-block align-text-middle"
						/>
						<div
							style={{
								marginLeft: '0.5rem',
								color: '#6066C1',
							}}
						>
							AI Recipes
						</div>
					</div>
				</Navbar.Brand>
				<hr style={{ width: '2px', height: '2rem', margin: '0', marginRight: '1rem' }} />
				<Navbar.Collapse id="responsive-navbar-nav" style={{}}>
					<Nav className="me-auto">
						{SIDEBAR_ITEMS.map((e: SidebarItem, i: number) => {
							return renderMenuItem(e, i);
						})}
					</Nav>
				</Navbar.Collapse>
				<div className="d-flex" style={{ marginRight: '1rem' }}>
					<UserAvatar
						userName={`${reduxUser.firstName} ${reduxUser.lastName}`}
						path={getPicturePath(reduxUser.profilePicture, 26)}
					/>
				</div>
				<ShoppingCartQuickInfo sidebarState={'closed'} />

				<li className="nav-item dropdown" style={{ listStyle: 'none', padding: '0px' }}>
					<div
						className="nav-link d-flex align-items-center"
						id="navbarUserProfile"
						role="button"
						data-bs-toggle="dropdown"
						aria-expanded="false"
					>
						<button
							className="btn btn-outline-dark"
							style={{ width: '2.8rem', height: '2.5rem', marginRight: '1rem' }}
						>
							<div
								className="icon-fade-wrapper"
								style={{ position: 'relative', width: '100%', height: '100%' }}
							>
								<FontAwesomeIcon
									icon={faMessagesQuestion}
									className={`icon-fade ${
										!showAltIcon ? 'icon-fade-in' : 'icon-fade-out'
									}`}
									style={{ position: 'absolute', left: 0, top: '0.3rem' }}
								/>
								<FontAwesomeIcon
									icon={faMicrochipAi}
									className={`icon-fade ${
										showAltIcon ? 'icon-fade-in' : 'icon-fade-out'
									}`}
									style={{ position: 'absolute', left: '0.1rem', top: '0.3rem' }}
								/>
							</div>
						</button>
					</div>
					<ul
						className="dropdown-menu dropdown-menu-end"
						aria-labelledby="navbarUserProfile"
					>
						<li>
							<NavLink to="/airflow/help" className="dropdown-item">
								{t('_general:HELP')}
							</NavLink>
						</li>
						<li>
							<NavLink to={'/airflow/knowledge-base'} className="dropdown-item">
								{t('admin:KNOWLEDGE_BASE')}
							</NavLink>
						</li>
					</ul>
				</li>
			</Container>
		</Navbar>
	);
}
